import React, { useState } from "react";
import { SlTab, SlTabGroup, SlTabPanel } from "@shoelace-style/shoelace/dist/react";
import FilterBar from "./FilterBar";
import SiteWiseTab from "./SiteWiseTab";
import CustWiseTab from "./CustWiseTab";
import GenerateScriptTab from "./GenerateScriptTab";
import EventsModal from "./EventsModal";

function PixelTrackingReports() {
  const [activeTab, setActiveTab] = useState("site");

  const [siteSearch, setSiteSearch] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");

  // Which level each tab is currently showing, so the filter bar knows which
  // search box is relevant right now.
  const [siteTabLevel, setSiteTabLevel] = useState("list");
  const [custTabLevel, setCustTabLevel] = useState("list");

  const [modal, setModal] = useState({ open: false, siteId: null, custId: null, eventName: null, searchTerm: null });
  const openModal = (siteId, custId, eventName) => setModal({ open: true, siteId, custId, eventName, searchTerm: null });
  const openSearchModal = (siteId, custId, searchTerm) =>
    setModal({ open: true, siteId, custId, eventName: null, searchTerm });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  // Custom alt/title search-to-column state, shared across both tabs.
  const [customColumns, setCustomColumns] = useState([]);
  const addCustomColumn = (term) => setCustomColumns((cols) => (cols.includes(term) ? cols : [...cols, term]));
  const removeCustomColumn = (term) => setCustomColumns((cols) => cols.filter((c) => c !== term));

  // Header-click sorting, replacing the old Sort By/Order dropdowns — clicking
  // the already-active header flips its order; clicking the other one
  // switches to it with a fresh descending default.
  const handleSortClick = (field) => {
    if (field === sort) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
  };

  // Site tab: site_id search drives level 1 (sites), cust_id search drives
  // level 2 (cust_ids for that site). Cust tab is the mirror.
  const siteSearchDisabled = activeTab === "site" ? siteTabLevel !== "list" : custTabLevel !== "drill";
  const custSearchDisabled = activeTab === "site" ? siteTabLevel !== "drill" : custTabLevel !== "list";

  return (
    <div className="p-4 max-w-full">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Pixel Tracking Reports</h2>
      </div>

      {activeTab !== "generate" && (
        <FilterBar
          onSiteSearchChange={setSiteSearch}
          siteSearchDisabled={siteSearchDisabled}
          onCustSearchChange={setCustSearch}
          custSearchDisabled={custSearchDisabled}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onAddCustomColumn={addCustomColumn}
        />
      )}

      <SlTabGroup onSlTabShow={(e) => setActiveTab(e.detail.name)}>
        <SlTab slot="nav" panel="site">
          Site ID wise
        </SlTab>
        <SlTab slot="nav" panel="cust">
          Cust ID wise
        </SlTab>
        <SlTab slot="nav" panel="generate">
          Generate Script
        </SlTab>

        <SlTabPanel name="site">
          {activeTab === "site" && (
            <SiteWiseTab
              key="site"
              siteSearch={siteSearch}
              custSearch={custSearch}
              dateRange={dateRange}
              sort={sort}
              order={order}
              onSortClick={handleSortClick}
              customColumns={customColumns}
              onRemoveCustomColumn={removeCustomColumn}
              onLevelChange={setSiteTabLevel}
              onOpenModal={openModal}
              onOpenSearchModal={openSearchModal}
            />
          )}
        </SlTabPanel>
        <SlTabPanel name="cust">
          {activeTab === "cust" && (
            <CustWiseTab
              key="cust"
              siteSearch={siteSearch}
              custSearch={custSearch}
              dateRange={dateRange}
              sort={sort}
              order={order}
              onSortClick={handleSortClick}
              customColumns={customColumns}
              onRemoveCustomColumn={removeCustomColumn}
              onLevelChange={setCustTabLevel}
              onOpenModal={openModal}
              onOpenSearchModal={openSearchModal}
            />
          )}
        </SlTabPanel>
        <SlTabPanel name="generate">{activeTab === "generate" && <GenerateScriptTab />}</SlTabPanel>
      </SlTabGroup>

      <EventsModal
        open={modal.open}
        siteId={modal.siteId}
        custId={modal.custId}
        eventName={modal.eventName}
        searchTerm={modal.searchTerm}
        onClose={closeModal}
      />
    </div>
  );
}

export default PixelTrackingReports;
